import { Box, Button, Typography } from '@mui/material';
import { useWidgetContext } from '@ptek/widget-core';
import { StickyFooter } from '../../../components/LayoutBoxes';
import { ComponentType, useContext, useMemo } from 'react';
import { TAB_IDS } from '../../../constants/app';
import { AppContext } from '../../../context/AppContext';
import { useAutoExportDocument } from '../../../features/Document/hooks/useAutoExportDocument';
import { createDocumentState } from '../../../features/Document/utils/documentContentUtils';
import { AmbientScribeContext, ContextDataItem } from '../../../types';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import {
  hasFeature,
  useUpdateNoteButtonLabel,
} from '../../../utils/feature-util';
import { useContextSelection } from '../hooks/useContextSelection';
import { contextPanelStyles } from '../styles/ContextPanelStyles';
import { isContextDataDirty, processLabel } from '../utils';
import { ContextItem } from './ContextItem';
import { FreeTextContext } from './FreeTextContext';
import { SelectContextItem } from './SelectContextItem';

export const ContextPanel: ComponentType<{
  handleTabChange: (tab: string) => void;
  lastGeneratedContext: ContextDataItem[] | null;
  onContextGenerated: (contextData: ContextDataItem[]) => void;
}> = ({ handleTabChange, lastGeneratedContext, onContextGenerated }) => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const { sessionModel, generateDocument } = useContext(AppContext);
  const { contextData } = useContextSelection();
  const { performAutoExport, isAutoExportEnabled, autoExportConfig } =
    useAutoExportDocument();
  const appointment = widgetContext.context?.appointment;
  const hasDocumentContext = hasFeature(FEATURE_FLAGS.DOCUMENT_CONTEXT);
  const updateNoteButtonLabel = useUpdateNoteButtonLabel();

  const isDirty = useMemo(
    () => isContextDataDirty(contextData, lastGeneratedContext),
    [contextData, lastGeneratedContext]
  );

  /**
   * Extract required context keys from the selected template
   * These are the context items that should be checked and disabled
   */
  const requiredContextKeys = useMemo(() => {
    const selectedTemplate = sessionModel.templates.find(
      (t) => t.id === sessionModel.selectedTemplateId
    );
    return selectedTemplate?.requiredContextKeys ?? [];
  }, [sessionModel.templates, sessionModel.selectedTemplateId]);

  const processedConfig = useMemo(() => {
    if (contextData.length === 0) return [];

    return contextData
      .filter((item) => !item.hidden)
      .map((item) => ({
        ...item,
        label: item.label
          ? processLabel(item.label, appointment)
          : String(item.key),
        children: item.children?.map((child) => ({
          ...child,
          label: child.label
            ? processLabel(child.label, appointment)
            : String(child.key),
        })),
      }));
  }, [appointment, contextData]);

  const handleRegenerateDocument = async () => {
    if (!autoExportConfig?.hideSoapNotesTab) {
      handleTabChange(TAB_IDS.document);
    }

    const generatedDocument = await generateDocument({
      templateId: sessionModel.selectedTemplateId,
      clearExistingDocuments: true,
    });

    if (generatedDocument) {
      onContextGenerated(contextData);

      if (isAutoExportEnabled) {
        const documentState = createDocumentState(generatedDocument);
        performAutoExport(documentState);
      }
    }
  };

  const hasSelectableContext = processedConfig.length > 0;

  /** Free text renders whenever documentContext is enabled, even without host-supplied items (NEB-101474); its hidden value is excluded from visible-item detection. */
  if (!hasSelectableContext && !hasDocumentContext) return null;

  const showRegenerateButton = isDirty && sessionModel.documents.length > 0;

  return (
    <Box sx={contextPanelStyles.mainContainer}>
      {hasSelectableContext && (
        <Typography variant='subtitle2' sx={contextPanelStyles.title}>
          Select context to include with the transcription:
        </Typography>
      )}

      <Box sx={contextPanelStyles.contextItemsContainer}>
        {processedConfig.map((item) => {
          if (item.type === 'select') {
            return <SelectContextItem key={item.key} item={item} />;
          }

          return (
            <ContextItem
              key={item.key}
              item={item}
              requiredContextKeys={requiredContextKeys}
            />
          );
        })}

        {hasDocumentContext && <FreeTextContext />}
      </Box>

      {showRegenerateButton && (
        <StickyFooter>
          <Button
            onClick={handleRegenerateDocument}
            disabled={sessionModel.isGenerating}
            variant='pill'
          >
            {sessionModel.isGenerating ? 'Updating...' : updateNoteButtonLabel}
          </Button>
        </StickyFooter>
      )}
    </Box>
  );
};
